class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    giftCart();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
     giftCart();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      giftCart();
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
     
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // Fallback shape; the parent's updateQuantity is overridden below.
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        const cartDrawer = document.querySelector('cart-drawer');
        const isEmpty = parsedState.item_count === 0;

        this.classList.toggle('is-empty', isEmpty);
        if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);

        const freshDrawer = new DOMParser()
          .parseFromString(parsedState.sections['cart-drawer'], 'text/html');

        if (isEmpty) {
          // Cart just went empty — do a full drawer inner replace so the
          // theme's empty-state UI renders correctly.
          const inner = document.querySelector('#CartDrawer .drawer__inner');
          const freshInner = freshDrawer.querySelector('.drawer__inner');
          if (inner && freshInner) inner.innerHTML = freshInner.innerHTML;
        } else {
          this._patchLineItems(freshDrawer);
          this._patchSubtotal(freshDrawer);
          this._patchShipbar(freshDrawer);
        }

        // Cart icon bubble
        try {
          const bubbleFresh = new DOMParser()
            .parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');
          const bubbleLocal = document.getElementById('cart-icon-bubble');
          if (bubbleFresh && bubbleLocal) bubbleLocal.innerHTML = bubbleFresh.innerHTML;
        } catch (e) { /* ignore */ }

        try { this.updateLiveRegions(line, parsedState.item_count); } catch (e) {}
        this.disableLoading();
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((o) => o.classList.add('hidden'));
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
        this.disableLoading();
      });
  }

  _patchLineItems(freshDoc) {
    const freshItems = freshDoc.querySelectorAll('.cart-item');
    const freshIds = new Set();
    freshItems.forEach((it) => it.dataset.id && freshIds.add(it.dataset.id));

    // Remove local items no longer present
    this.querySelectorAll('.cart-item').forEach((local) => {
      if (local.dataset.id && !freshIds.has(local.dataset.id)) local.remove();
    });

    // Patch/append remaining items
    freshItems.forEach((freshItem) => {
      const local = this.querySelector(`.cart-item[data-id="${freshItem.dataset.id}"]`);
      if (!local) {
        const tbody = this.querySelector('.cart-items tbody');
        if (tbody) tbody.appendChild(freshItem);
        return;
      }
      // Line price
      const fPrice = freshItem.querySelector('.cart-item__price-wrapper');
      const lPrice = local.querySelector('.cart-item__price-wrapper');
      if (fPrice && lPrice && lPrice.innerHTML !== fPrice.innerHTML) {
        lPrice.innerHTML = fPrice.innerHTML;
      }
      // Qty input value only (leave DOM structure intact — no flicker)
      const fInput = freshItem.querySelector('.quantity__input');
      const lInput = local.querySelector('.quantity__input');
      if (fInput && lInput && lInput.value !== fInput.value) {
        lInput.value = fInput.value;
      }
      // Discounts (in case an auto-discount toggles at a threshold)
      const fDisc = freshItem.querySelector('.discounts');
      const lDisc = local.querySelector('.discounts');
      if (fDisc && lDisc && lDisc.innerHTML !== fDisc.innerHTML) {
        lDisc.innerHTML = fDisc.innerHTML;
      }
      // Image, name, remove-button: intentionally NOT touched → no flicker
    });
  }

  _patchSubtotal(freshDoc) {
    const fVal = freshDoc.querySelector('.totals__subtotal-value');
    const lVal = document.querySelector('cart-drawer .totals__subtotal-value');
    if (fVal && lVal && lVal.innerHTML !== fVal.innerHTML) {
      lVal.innerHTML = fVal.innerHTML;
    }
    const fCartDisc = freshDoc.querySelector('.cart-drawer__footer .discounts');
    const lCartDisc = document.querySelector('cart-drawer .cart-drawer__footer .discounts');
    if (fCartDisc && lCartDisc && lCartDisc.innerHTML !== fCartDisc.innerHTML) {
      lCartDisc.innerHTML = fCartDisc.innerHTML;
    }
  }

  _patchShipbar(freshDoc) {
    const fresh = freshDoc.querySelector('.fl-shipbar');
    const local = document.querySelector('cart-drawer .fl-shipbar');
    if (fresh && local) {
      const fText = fresh.querySelector('.fl-shipbar__text');
      const lText = local.querySelector('.fl-shipbar__text');
      if (fText && lText) lText.innerHTML = fText.innerHTML;
      const fFill = fresh.querySelector('.fl-shipbar__fill');
      const lFill = local.querySelector('.fl-shipbar__fill');
      if (fFill && lFill) lFill.style.width = fFill.style.width;
      local.classList.toggle('is-complete', fresh.classList.contains('is-complete'));
    } else if (!fresh && local) {
      local.remove();
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function giftCart() {
  const cartItems = document.querySelectorAll('tr.cart-item');
    cartItems.forEach(cartItem => {        
        const dataId = cartItem.getAttribute('data-id');
        const drawerFooter = document.querySelector(`#Gift-Wrapper[data-id="${dataId}"]`);  
      if(!drawerFooter) return;
      const drawerFooterID = drawerFooter.getAttribute('data-id');
        if(drawerFooterID == dataId) {            
            drawerFooter.classList.add('Gift-Wrapper__hide'); 
        }
    });
}

